import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { range, reverse, isUndefined, values } from 'lodash';
import { css } from 'styled-components/macro';
import Picker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { Row, Col } from 'bs';
import Icon from 'components/Icon';
import dates from 'utils/dates';

const datePickerCss = css`
    .react-datepicker-popper {
        z-index: 3000;
    }
`;

const inputStyle = {
    control: base => ({
        ...base,
        borderRadius: 0,
        backgroundColor: 'white',
    }),
};

const isAlpha = code => code >= 65 && code <= 90;

const isNumber = code => (code >= 48 && code <= 57) || (code >= 96 && code <= 105);

// backspace, tab, enter
const isUtilKey = code => code === 8 || code === 9 || code === 13;

class CalendarButton extends Component {
    render() {
        return (
            <Icon
                name='calendar'
                onClick={this.props.onClick}
                className='form-control'
                style={{height: 27, width: 22}}
            />
        );
    }
}

class DatePicker extends Component {
    static propTypes = {
        min: PropTypes.oneOfType([
            PropTypes.instanceOf(Date),
            PropTypes.string,
            PropTypes.object,
        ]).isRequired,
        max: PropTypes.oneOfType([
            PropTypes.instanceOf(Date),
            PropTypes.string,
            PropTypes.object,
        ]).isRequired,
        reverseYears: PropTypes.bool,
        name: PropTypes.string.isRequired,
        onChange: PropTypes.func.isRequired,
        label: PropTypes.string,
        onBlur: PropTypes.func,
        errorText: PropTypes.string,
        helpText: PropTypes.string,
        initialValue: PropTypes.oneOfType([
            PropTypes.instanceOf(Date),
            PropTypes.string,
            PropTypes.object,
        ]),
        showCalendar: PropTypes.bool,
        menuPositn: PropTypes.string,
        disabled: PropTypes.bool,
    };
    static defaultProps = {
        menuPosition: 'absolute', // Select's default, but fixed is better in modal
    };

    constructor(props) {
        super(props);
        let initialState = {
            year: undefined,
            month: undefined,
            day: undefined,
            calendarDatePickerDate: undefined,
            focused: false,
        };

        let initialValid = false;

        if (props.initialValue) {
            const initialDate = dates.parseDate(props.initialValue);
            if (initialDate) {
                initialState.year = initialDate.getFullYear();
                initialState.month = initialDate.getMonth();
                initialState.day = initialDate.getUTCDate();
                initialValid = true;
                initialState.calendarDatePickerDate = initialDate;
            }
        }

        this.state = { ...initialState, valid: initialValid };
        this.handleCalendarDatePickerChange = this.handleCalendarDatePickerChange.bind(this);
    }

    updateDate(input) {
        const data = {
            month: !isUndefined(input.month) ? input.month : this.state.month,
            year: input.year ? input.year : this.state.year,
            day: input.day ? input.day : this.state.day,
        };
        const date = dates.parseDate(data);
        const allPresent = values(data).filter(val => !isUndefined(val)).length === 3;
        const valid = Boolean(allPresent && date);

        this.setState({
            ...data,
            valid,
            calendarDatePickerDate: valid ? date : undefined
        });

        if (!valid) {
            this.props.onChange('');
        } else {
            this.props.onChange(dates.formatDate(date.toISOString(), 'YYYY-MM-DD'));
        }
    }

    getDateRange() {
        return {
            min: dates.parseDate(this.props.min),
            max: dates.parseDate(this.props.max),
        };
    }

    getMonthChoices() {
        const { min, max } = this.getDateRange();
        const minMonth = min.getMonth();
        const maxMonth = max.getMonth();
        let monthChoices = dates.monthChoices;

        // filter out invalid months based on min/max dates
        if (this.state.year === min.getFullYear()) {
            //monthChoices = monthChoices.filter(i => i.value >= min.month());
            monthChoices = monthChoices
                .map(i => {
                    if (i.value < minMonth) {
                        if (i.value === minMonth - 1) {
                            return {
                                value: null,
                                label: `Min: ${dates.formatDate(
                                    min.toISOString(),
                                    'YYYY-MM-DD'
                                )}`,
                                isDisabled: true,
                            };
                        } else {
                            return null;
                        }
                    } else {
                        return i;
                    }
                })
                .filter(i => i);
        }

        if (this.state.year === max.getFullYear()) {
            monthChoices = monthChoices
                .map(i => {
                    if (i.value > maxMonth) {
                        if (i.value === maxMonth + 1) {
                            return {
                                value: null,
                                label: `Max: ${dates.formatDate(
                                    max.toISOString(),
                                    'YYYY-MM-DD'
                                )}`,
                                isDisabled: true,
                            };
                        } else {
                            return null;
                        }
                    } else {
                        return i;
                    }
                })
                .filter(i => i);
        }

        return monthChoices;
    }


    getYearChoices() {
        const { min, max } = this.getDateRange();
        const minYear = min.getFullYear();
        const maxYear = max.getFullYear();

        let years = range(minYear, maxYear + 1);
        if (this.props.reverseYears) {
            years = reverse(years);
        }

        return years.map(year => ({
            value: year,
            label: year,
        }));
    }

    getDayChoices() {
        let dayChoices = [];

        const { year, month } = this.state;

        if (isUndefined(year) || isUndefined(month)) {
            return dayChoices;
        }

        const daysInMonth = dates.daysInMonth(year, month);
        const { min, max } = this.getDateRange();
        const minDay = min.getDate();
        const maxDay = max.getDate();

        dayChoices = range(1, daysInMonth + 1).map(day => ({
            value: day,
            label: day,
        }));

        // filter out invalid days based on min/max dates
        if (year === min.getFullYear() && month === min.getMonth()) {
            dayChoices = dayChoices
                .map(i => {
                    if (i.value < minDay) {
                        if (i.value === minDay - 1) {
                            return {
                                value: null,
                                label: `Min: ${dates.formatDate(
                                    min.toISOString(),
                                    'YYYY-MM-DD'
                                )}`,
                                isDisabled: true,
                            };
                        } else {
                            return null;
                        }
                    } else {
                        return i;
                    }
                })
                .filter(i => i);
        }

        if (year === max.getFullYear() && month === max.getMonth()) {
            dayChoices = dayChoices
                .map(i => {
                    if (i.value > max.getDate()) {
                        if (i.value === maxDay + 1) {
                            return {
                                value: null,
                                label: `Max: ${dates.formatDate(
                                    max.toISOString(),
                                    'YYYY-MM-DD'
                                )}`,
                                isDisabled: true,
                            };
                        } else {
                            return null;
                        }
                    } else {
                        return i;
                    }
                })
                .filter(i => i);
        }

        return dayChoices;
    }

    handleCalendarDatePickerChange(value) {
        const date = dates.parseDate(value);
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();

        this.updateDate({ year, month, day });
    }

    render() {
        const years = this.getYearChoices();
        const year =
            years.filter(option => option.value === this.state.year)[0] || null;
        const months = this.getMonthChoices();
        const month =
            months.filter(option => option.value === this.state.month)[0] || null;
        const days = this.getDayChoices();
        const day = days.filter(option => option.value === this.state.day)[0] || null;
        const { label, menuPosition } = this.props;

        let onBlur = e => {
            e.target.name = this.props.name;
            this.setState({ focused: false });
            if (this.props.onBlur) {
                this.props.onBlur(e);
            }
        };
        onBlur = onBlur.bind(this);

        const onFocus = () => this.setState({ focused: true });
        const { min, max } = this.getDateRange();

        return (
            <div className='form-group react-input-form-group' css={datePickerCss}>
                {label && <label>{label}</label>}
                <Row gutters={false}>
                    <Col className='col-datepicker-year' xs={this.props.showCalendar ? 3 : 4} sm={4}>
                        <Select
                            name='year'
                            options={years}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            onChange={(option, action) =>
                                this.updateDate({ year: option.value })
                            }
                            onKeyDown={e => {
                                // only allow number input (and backspace):
                                if (!(isNumber(e.keyCode) || isUtilKey(e.keyCode))) {
                                    e.preventDefault();
                                }
                            }}
                            value={year}
                            placeholder='Year'
                            className='mr-2'
                            styles={inputStyle}
                            isDisabled={this.props.disabled}
                            classNamePrefix='year'
                            blurInputOnSelect={false}
                            menuPosition={menuPosition}
                        />
                    </Col>
                    <Col className='col-datepicker-month' xs={this.props.showCalendar ? 4 : 5}>
                        <Select
                            name='month'
                            options={months}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            onChange={(option, action) =>
                                this.updateDate({ month: option.value })
                            }
                            onKeyDown={e => {
                                // only allow alpha input (and backspace):
                                if (!(isAlpha(e.keyCode) || isUtilKey(e.keyCode))) {
                                    e.preventDefault();
                                }
                            }}
                            value={month}
                            placeholder='Month'
                            className='mr-2'
                            styles={inputStyle}
                            isDisabled={this.props.disabled}
                            classNamePrefix='month'
                            blurInputOnSelect={false}
                            menuPosition={menuPosition}
                        />
                    </Col>
                    <Col className='col-datepicker-day' xs={3}>
                        <Select
                            name='day'
                            options={days}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            onChange={(option, action) =>
                                this.updateDate({ day: option.value })
                            }
                            onKeyDown={e => {
                                // only allow number input (and backspace):
                                if (!(isNumber(e.keyCode) || isUtilKey(e.keyCode))) {
                                    e.preventDefault();
                                }
                            }}
                            value={day}
                            placeholder='Day'
                            className={this.props.showCalendar ? 'mr-3' : 'mr-0'}
                            styles={inputStyle}
                            noOptionsMessage={inputValue =>
                                'Please select a month and year'
                            }
                            isDisabled={this.props.disabled}
                            classNamePrefix='day'
                            blurInputOnSelect={false}
                            menuPosition={menuPosition}
                        />
                    </Col>
                    {this.props.showCalendar && (
                        <Col className='col-datepicker-calendar' xs={1}>
                            <Picker
                                name='calendar'
                                minDate={min}
                                maxDate={max}
                                onChange={this.handleCalendarDatePickerChange}
                                selected={this.state.calendarDatePickerDate}
                                dateFormat='yyyy-MM-dd'
                                customInput={<CalendarButton />}
                                disabled={this.props.disabled}
                            />
                        </Col>
                    )}
                </Row>
                {this.props.errorText && !this.state.focused && (
                    <small className='form-text text-danger'>
                        {this.props.errorText}
                    </small>
                )}
                {this.props.helpText && (
                    <small className='form-text text-muted'>
                        {this.props.helpText}
                    </small>
                )}
            </div>
        );
    }
}

export default DatePicker;
